"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { useRouter, useSearchParams } from "next/navigation"
import { useInfiniteQuery } from "@tanstack/react-query"
import { getTreatmentCaseListClient } from "@/entities/treatment-case/api/treatment-case.client"
import { useGetLocale } from "@/shared/hooks/useGetLocale"
import type { ApiResponse } from "@/shared/types/interface/api"
import {
  TREATMENT_CASE_SORT,
  TREATMENT_CASE_SORT_LABEL,
  TREATMENT_CASE_SORT_OPTIONS,
  type TreatmentCaseFilterResponse,
  type TreatmentCaseListResponse,
  type TreatmentCaseSort,
} from "@/shared/types/type/treatment"
import { BackIcon, HomeIcon, ShareIcon } from "@/shared/ui/atoms"
import useIconAction from "@/shared/hooks/useIconAction"
import ContentRecentCaseCard from "@/widgets/treatment-case/ui/molecules/ContentRecentCaseCard"

const DENTISTRY_CATEGORY_ID = 4

interface DentalTreatmentCaseTemplatesProps {
  filterList: TreatmentCaseFilterResponse
  initialCategoryId?: number
  initialSubjectId?: number
  initialSort: TreatmentCaseSort
  initialListResponse: ApiResponse<TreatmentCaseListResponse>
}

const DentalTreatmentCaseTemplates = ({
  filterList,
  initialCategoryId,
  initialSubjectId,
  initialSort,
  initialListResponse,
}: DentalTreatmentCaseTemplatesProps) => {
  const locale = useGetLocale()
  const router = useRouter()
  const searchParams = useSearchParams()
  const { handleHomeRoute, handleShareRoute } = useIconAction()
  const isSubjectRoute = initialSubjectId !== undefined
  const treatmentCategoryId =
    isSubjectRoute || searchParams.get("categoryId") !== "all"
      ? DENTISTRY_CATEGORY_ID
      : undefined
  const selectedSubjectId = initialSubjectId ?? 0
  const [selectedSort, setSelectedSort] = useState<TreatmentCaseSort>(initialSort)
  const loadMoreRef = useRef<HTMLDivElement | null>(null)

  const subjectList = useMemo(() => {
    if (!treatmentCategoryId) return []

    return (
      filterList.find(
        (category) => category.treatmentCategoryId === treatmentCategoryId,
      )?.treatmentSubjects ?? []
    )
  }, [filterList, treatmentCategoryId])

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetchingNextPage,
    isLoading,
    refetch,
  } = useInfiniteQuery<ApiResponse<TreatmentCaseListResponse>, Error>({
    queryKey: [
      "dentalTreatmentCaseList",
      treatmentCategoryId ?? "all",
      selectedSubjectId,
      selectedSort,
    ],
    queryFn: ({ pageParam }) =>
      getTreatmentCaseListClient(
        treatmentCategoryId,
        selectedSubjectId || undefined,
        selectedSort,
        pageParam as number,
      ),
    initialPageParam: 1,
    initialData:
      treatmentCategoryId === initialCategoryId &&
      selectedSubjectId === (initialSubjectId ?? 0) &&
      selectedSort === initialSort
        ? {
          pages: [initialListResponse],
          pageParams: [1],
        }
        : undefined,
    getNextPageParam: (lastPage, pages) => {
      const loadedCount = pages.reduce(
        (count, page) => count + page.result.contents.length,
        0,
      )

      return loadedCount < lastPage.result.total ? pages.length + 1 : undefined
    },
    staleTime: 5 * 60 * 1000,
  })

  useEffect(() => {
    const target = loadMoreRef.current
    if (!target || !hasNextPage || isFetchingNextPage) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) void fetchNextPage()
      },
      { rootMargin: "200px" },
    )

    observer.observe(target)
    return () => observer.disconnect()
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  const treatmentCases = data?.pages.flatMap((page) => page.result.contents) ?? []
  const subjectClassName = (subjectId: number) =>
    `shrink-0 whitespace-nowrap rounded-full px-[14px] py-2 text-[15px] font-medium leading-[22px] ${
      selectedSubjectId === subjectId
        ? "bg-gray-700 text-white"
        : "bg-gray-100 text-gray-800"
    }`

  const dentalTreatmentCasePath = `/${locale}/treatment-case/dental`
  const sortQuery =
    selectedSort === TREATMENT_CASE_SORT.VIEW_COUNT_DESC ? "" : `sort=${selectedSort}`
  const allSubjectHref =
    searchParams.get("categoryId") === "all"
      ? `${dentalTreatmentCasePath}?categoryId=all${sortQuery ? `&${sortQuery}` : ""}`
      : `${dentalTreatmentCasePath}${sortQuery ? `?${sortQuery}` : ""}`

  return (
    <section className="min-h-screen bg-white pb-16 font-pretendard">
      <header className="sticky top-0 z-[101] flex h-[54px] items-center justify-between bg-white px-4">
        <button
          type="button"
          onClick={() => router.push(`/${locale}/treatment-case`)}
          aria-label="콘텐츠 화면으로 돌아가기"
        >
          <BackIcon size={24} className="text-gray-900" />
        </button>
        <h1 className="absolute left-1/2 -translate-x-1/2 text-[18px] font-semibold leading-[25px] text-gray-900">
          치료사례
        </h1>
        <div className="flex items-center gap-4">
          <button type="button" onClick={handleHomeRoute} aria-label="홈으로 이동">
            <HomeIcon size={24} className="text-gray-900" />
          </button>
          <button type="button" onClick={handleShareRoute} aria-label="페이지 공유">
            <ShareIcon size={24} className="text-gray-900" />
          </button>
        </div>
      </header>

      <div className="sticky top-[54px] z-[100] flex h-16 items-center gap-1.5 bg-white py-3 pl-4">
        <div className="relative flex h-[38px] shrink-0 items-center gap-1 rounded-full border border-gray-200 bg-white py-1.5 pl-3 pr-2.5">
          <span className="whitespace-nowrap text-[15px] font-normal leading-[22px] text-gray-900">
            {TREATMENT_CASE_SORT_LABEL[selectedSort]}
          </span>
          <span className="relative flex size-4 shrink-0 items-center justify-center overflow-hidden" aria-hidden>
            <Image
              src="/figma/content/filter-arrow.svg"
              alt=""
              width={7}
              height={12}
              className="rotate-90"
            />
          </span>
          <select
            aria-label="치료사례 정렬"
            value={selectedSort}
            onChange={(event) => setSelectedSort(event.target.value as TreatmentCaseSort)}
            className="absolute inset-0 size-full cursor-pointer appearance-none opacity-0"
          >
            {TREATMENT_CASE_SORT_OPTIONS.map((sort) => (
              <option key={sort} value={sort}>
                {TREATMENT_CASE_SORT_LABEL[sort]}
              </option>
            ))}
          </select>
        </div>

        <div className="scrollbar-hide min-w-0 flex-1 overflow-x-auto pr-4">
          <div className="flex w-max gap-1.5">
            <Link
              href={allSubjectHref}
              className={subjectClassName(0)}
              aria-current={selectedSubjectId === 0 ? "page" : undefined}
            >
              전체
            </Link>
            {subjectList.map((subject) => (
              <Link
                key={subject.treatmentSubjectId}
                href={`${dentalTreatmentCasePath}/${subject.treatmentSubjectId}${sortQuery ? `?${sortQuery}` : ""}`}
                className={subjectClassName(subject.treatmentSubjectId)}
                aria-current={
                  selectedSubjectId === subject.treatmentSubjectId ? "page" : undefined
                }
              >
                {subject.treatmentSubjectName}
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="px-5">
        {isLoading ? (
          <DentalCaseListSkeleton />
        ) : isError ? (
          <CaseListError onRetry={() => void refetch()} />
        ) : treatmentCases.length > 0 ? (
          treatmentCases.map((item, index) => (
            <ContentRecentCaseCard
              key={item.treatmentCaseId}
              item={item}
              locale={locale}
              priority={index === 0}
            />
          ))
        ) : (
          <p className="flex min-h-48 items-center justify-center text-[14px] text-gray-400">
            등록된 치료사례가 없습니다.
          </p>
        )}
        <div ref={loadMoreRef} className="h-1" aria-hidden />
        {isFetchingNextPage && (
          <p className="py-4 text-center text-[14px] text-gray-400">불러오는 중</p>
        )}
      </div>
    </section>
  )
}

const DentalCaseListSkeleton = () => (
  <div aria-label="치과 치료사례를 불러오는 중">
    {[0, 1, 2, 3, 4].map((index) => (
      <div key={index} className="flex h-32 items-center border-b border-gray-100 py-5">
        <div className="mr-3 flex flex-1 flex-col gap-4">
          <div className="h-5 animate-pulse rounded bg-gray-100" />
          <div className="h-5 w-3/4 animate-pulse rounded bg-gray-100" />
        </div>
        <div className="size-20 animate-pulse rounded-lg bg-gray-100" />
      </div>
    ))}
  </div>
)

const CaseListError = ({ onRetry }: { onRetry: () => void }) => (
  <div className="flex min-h-48 flex-col items-center justify-center gap-3 text-[14px] text-gray-500">
    <p>치료사례를 불러오지 못했습니다.</p>
    <button
      type="button"
      onClick={onRetry}
      className="rounded-full border border-gray-200 px-4 py-2 font-medium text-gray-800"
    >
      다시 시도
    </button>
  </div>
)

export default DentalTreatmentCaseTemplates
